/**
 * Turn release feature descriptions into display bullet items.
 */

/** Split commit-style subjects like "Add A, B, and C" into separate items. */
export function splitCompoundSubject(subject: string): string[] {
  const trimmed = subject.trim();
  if (!trimmed) return [];

  const andMatch = trimmed.match(/^(.+),\s*and\s+(.+)$/i);
  if (andMatch) {
    return [
      ...splitCompoundSubject(andMatch[1]),
      ...splitCompoundSubject(andMatch[2]),
    ];
  }

  if (trimmed.includes(', ')) {
    return trimmed.split(/,\s+/).map((s) => s.trim()).filter(Boolean);
  }

  return [trimmed];
}

/** True when text reads like a commit subject (short list), not prose. */
function looksLikeCompoundSubject(text: string): boolean {
  const t = text.trim();
  if (!t || t.length > 160) return false;
  if (/[.!?]\s/.test(t)) return false;
  return /,\s*(and\s+)?/i.test(t);
}

/** Split prose into sentence-sized bullet items. */
export function splitProseSentences(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  if (!/[.!?]\s+/.test(trimmed)) {
    return [trimmed];
  }

  return trimmed
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Parse stored description / bullet lines into a clean list for UI. */
export function parseReleaseDescriptionBullets(
  description: string,
  explicitBullets?: string[],
): string[] {
  if (explicitBullets?.length) {
    return explicitBullets.map((b) => b.trim()).filter(Boolean);
  }

  const text = (description || '').trim();
  if (!text) return [];

  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  const fromMarkers = lines
    .map((line) => line.replace(/^[•\-*]\s+/, '').replace(/^\d+\.\s+/, '').trim())
    .filter(Boolean);

  if (fromMarkers.length > 1) return fromMarkers;

  const single = fromMarkers.length === 1 ? fromMarkers[0] : text;

  // Never comma-split flowing prose — use sentences instead.
  if (looksLikeCompoundSubject(single)) {
    const compound = splitCompoundSubject(single);
    if (compound.length > 1) return compound;
  }

  if (single.length > 60) {
    const sentences = splitProseSentences(single);
    if (sentences.length > 1) return sentences;
  }

  return [single];
}
